import { createPortal } from "react-dom";
import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { X, ArrowRight, ArrowLeft } from "lucide-react";
import api from "@/lib/api";

const LOGO_URL =
  "/logo-icon.png";

const buildSteps = (s) => [
  {
    type: "splash",
    title: "Meet Decline Drive",
    subtitle:
      "The automatic way to recover declined service revenue — every night, with zero clicks.",
    cta: "Start the tour",
  },
  {
    type: "highlight",
    selector: '[data-testid="kpi-declined"]',
    title: "Your recovery scoreboard",
    body: "Every declined service, text sent, live-person call, and appointment scheduled — at a glance.",
  },
  {
    type: "highlight",
    selector: '[data-testid^="advisor-row-"]',
    title: "Rank every advisor",
    body: "See who converts declined services best. Coach the rest of the team using real numbers instead of gut feel.",
  },
  {
    type: "highlight",
    selector: '[data-testid="filter-status"]',
    title: "Every decline, tracked",
    body: "Filter the pipeline by advisor and by status — from Declined all the way to Scheduled.",
  },
  {
    type: "highlight",
    selector: '[data-testid="import-nightly-btn"]',
    title: "The DMS drops in here",
    body: "Nightly decline export lands here. Or wire up SFTP and the file pulls itself — no clicks.",
  },
  {
    type: "highlight",
    selector: '[data-testid="run-campaign-btn"]',
    title: "Auto-send nightly",
    body: `The ${s?.stage_1_discount ?? 30}% (Day ${s?.stage_1_days ?? 7}) and ${s?.stage_2_discount ?? 40}% (Day ${s?.stage_2_days ?? 15}) texts fire nightly during your quiet-hours window — automatically. This button is only for catching up mid-day.`,
  },
  {
    type: "highlight",
    selector: '[data-testid="nav-settings"]',
    title: "Edit anything",
    body: "Change discount percentages, the exact SMS wording, dealership phone — all in Settings.",
  },
  {
    type: "splash",
    title: "You're ready to recover revenue.",
    subtitle:
      "Twilio + your DMS SFTP = fully hands-off within a week. Ayers Fixed Operations can have you live by end of business.",
    cta: "Close tour",
  },
];

export default function GuidedTour({ open, onClose }) {
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const [settings, setSettings] = useState(null);
  const STEPS = useMemo(() => buildSteps(settings), [settings]);
  const step = STEPS[i];

  useEffect(() => {
    if (!open) return;
    setI(0);
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open || !step || step.type !== "highlight") {
      setRect(null);
      return;
    }
    let cancelled = false;

    const attempt = (tries = 0) => {
      if (cancelled) return;
      const el = document.querySelector(step.selector);
      if (!el) {
        // Target isn't in the DOM (e.g. Nightly Import button is hidden when
        // demoMode is off, sidebar item is collapsed). Auto-advance so the tour
        // never gets stuck on a blank "Loading step…" screen.
        if (tries < 6) {
          setTimeout(() => attempt(tries + 1), 150);
          return;
        }
        setI((prev) => (prev < STEPS.length - 1 ? prev + 1 : prev));
        return;
      }
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(() => {
        if (cancelled) return;
        const r = el.getBoundingClientRect();
        // In case scroll hasn't settled, retry once with a slightly longer wait
        if (r.width === 0 && r.height === 0 && tries < 4) {
          setTimeout(() => attempt(tries + 1), 200);
          return;
        }
        setRect(r);
      }, 400);
    };

    attempt();
    const onResize = () => {
      const el = document.querySelector(step.selector);
      if (el) setRect(el.getBoundingClientRect());
    };
    window.addEventListener("resize", onResize);
    return () => {
      cancelled = true;
      window.removeEventListener("resize", onResize);
    };
  }, [i, open, step]);

  if (!open || !step) return null;

  const next = () => (i === STEPS.length - 1 ? onClose() : setI(i + 1));
  const prev = () => setI(Math.max(0, i - 1));

  return createPortal(
    <div className="fixed inset-0 z-[100]" data-testid="guided-tour">
      {step.type === "splash" && <div className="absolute inset-0 bg-black/80" />}

      {step.type === "highlight" && rect && (
        <div
          className="absolute rounded-lg ring-4 ring-primary pointer-events-none"
          style={{
            top: rect.top - 8,
            left: rect.left - 8,
            width: rect.width + 16,
            height: rect.height + 16,
            boxShadow: "0 0 0 9999px rgba(0,0,0,0.7)",
            transition: "top 200ms ease, left 200ms ease, width 200ms ease, height 200ms ease",
            WebkitBackfaceVisibility: "hidden",
            backfaceVisibility: "hidden",
            WebkitTransform: "translate3d(0,0,0)",
            transform: "translate3d(0,0,0)",
          }}
        />
      )}

      {step.type === "splash" ? (
        <div className="absolute inset-0 grid place-items-center p-6">
          <motion.div
            key={"splash-" + i}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            className="max-w-md w-full text-center bg-card border border-border rounded-xl p-8 shadow-2xl"
            style={{
              willChange: "auto",
              WebkitBackfaceVisibility: "hidden",
              backfaceVisibility: "hidden",
              WebkitTransform: "translate3d(0,0,0)",
              transform: "translate3d(0,0,0)",
            }}
          >
            <div className="inline-block p-1.5 bg-[hsl(var(--gold))] rounded-lg shadow-lg mb-4 mx-auto">
              <img
                src={LOGO_URL}
                alt="Decline Drive"
                className="w-24 h-24 object-contain rounded bg-white"
              />
            </div>
            <div className="font-mono-label text-muted-foreground">Decline Drive™</div>
            <h2 className="font-display font-black text-3xl mt-2 tracking-tighter">
              {step.title}
            </h2>
            <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{step.subtitle}</p>
            <Button
              onClick={next}
              className="mt-6 w-full"
              data-testid={`tour-splash-${i}-cta`}
            >
              {step.cta}
              <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2} />
            </Button>
            <button
              onClick={onClose}
              className="text-xs text-muted-foreground mt-3 hover:underline"
              data-testid="tour-skip-btn"
            >
              {i === 0 ? "Skip tour" : "Close"}
            </button>
            <div className="mt-4 text-[0.6rem] text-muted-foreground">
              © {new Date().getFullYear()} Decline Drive™ by Ayers Enterprises
            </div>
          </motion.div>
        </div>
      ) : rect ? (
        <motion.div
          key={"tip-" + i}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          className="absolute bg-card border border-border rounded-lg p-5 shadow-2xl w-[360px]"
          style={{
            top: Math.min(rect.bottom + 16, window.innerHeight - 220),
            left: Math.min(Math.max(rect.left, 20), window.innerWidth - 380),
            willChange: "auto",
            WebkitBackfaceVisibility: "hidden",
            backfaceVisibility: "hidden",
            WebkitTransform: "translate3d(0,0,0)",
            transform: "translate3d(0,0,0)",
          }}
        >
          <div className="font-mono-label text-primary">
            Step {i} of {STEPS.length - 2}
          </div>
          <h3 className="font-display font-bold text-lg mt-1 tracking-tight">{step.title}</h3>
          <p className="text-sm text-foreground/80 mt-2 leading-relaxed">{step.body}</p>
          <div className="flex items-center gap-2 mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={prev}
              disabled={i === 0}
              data-testid="tour-prev-btn"
            >
              <ArrowLeft className="w-4 h-4" strokeWidth={2} />
            </Button>
            <Button size="sm" onClick={next} className="flex-1" data-testid="tour-next-btn">
              Next
              <ArrowRight className="w-4 h-4 ml-1" strokeWidth={2} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              data-testid="tour-close-btn"
            >
              <X className="w-4 h-4" strokeWidth={2} />
            </Button>
          </div>
        </motion.div>
      ) : (
        <div className="absolute top-24 left-1/2 -translate-x-1/2 bg-card border border-border rounded-lg p-4 shadow-2xl">
          <p className="text-sm text-muted-foreground">Getting the next stop ready…</p>
        </div>
      )}
    </div>,
    document.body
  );
}
